/**
 * Button — shadcn/ui cva-based implementation.
 * Replaces the previous circular re-export from @opal/components.
 * Supports all Opal ButtonProps: prominence, leftIcon, rightIcon, icon,
 * isLoading, tooltip, href, width, size, variant.
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Link } from "react-router-dom";
import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 font-main-ui-action rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500",
        secondary:
          "bg-background-neutral-02 text-text-04 hover:bg-background-neutral-03 border border-border-01",
        outline:
          "bg-transparent text-text-04 hover:bg-background-neutral-02 border border-border-02",
        ghost:
          "bg-transparent text-text-03 hover:bg-background-tint-01",
        danger:
          "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500",
        plain:
          "bg-transparent text-text-03 hover:text-text-04",
        tertiary:
          "bg-transparent text-text-03 hover:bg-background-tint-01 hover:text-text-04",
        internal:
          "bg-transparent text-text-02 hover:text-text-03",
        "select-light":
          "bg-background-neutral-01 text-text-03 hover:bg-background-tint-01 border border-border-01",
      },
      size: {
        sm: "h-7 px-2.5 py-1 text-sm",
        md: "h-9 px-4 py-2",
        lg: "h-10 px-6 py-2 text-base",
        icon: "h-8 w-8 p-1.5",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger"
  | "plain"
  | "tertiary"
  | "internal"
  | "select-light";

export type ButtonProminence = "primary" | "secondary" | "tertiary" | "internal";

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">,
    VariantProps<typeof buttonVariants> {
  children?: React.ReactNode;
  /** Opal prominence scale, maps to variant */
  prominence?: ButtonProminence;
  /** Width: auto (default) or full */
  width?: "auto" | "full";
  /** Icon on the left side */
  leftIcon?: React.ElementType;
  /** Icon on the right side */
  rightIcon?: React.ElementType;
  /** Icon-only mode (no children) */
  icon?: React.ElementType;
  /** Show loading spinner */
  isLoading?: boolean;
  /** Navigate to URL (renders as Link or <a>) */
  href?: string;
  target?: string;
  /** Tooltip text shown on hover. Also used as aria-label for icon-only buttons */
  tooltip?: string;
  /** Render as child slot (Radix asChild) */
  asChild?: boolean;
  interaction?: "rest" | "hover" | "active";
}

function resolveVariant(
  prominence: ButtonProminence | undefined,
  variant: ButtonVariant | null | undefined
) {
  if (prominence === "tertiary") return "tertiary" as const;
  if (prominence === "secondary") return "secondary" as const;
  if (prominence === "internal") return "internal" as const;
  return (variant ?? "primary") as ButtonVariant;
}

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      className,
      variant,
      prominence,
      size = "md",
      width = "auto",
      asChild = false,
      children,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      icon: Icon,
      isLoading = false,
      href,
      target,
      tooltip,
      disabled,
      type = "button",
      interaction: _interaction,
      ...rest
    },
    ref
  ) => {
    const effectiveVariant = resolveVariant(prominence, variant);
    const isIconOnly = !children && (!!Icon || (!LeftIcon && !RightIcon));
    const effectiveSize = isIconOnly && !children ? "icon" : size;

    const derivedAriaLabel =
      isIconOnly && !(rest as Record<string, unknown>)["aria-label"] && tooltip
        ? tooltip
        : (rest as Record<string, unknown>)["aria-label"] as string | undefined;

    if (
      process.env.NODE_ENV !== "production" &&
      isIconOnly &&
      !derivedAriaLabel
    ) {
      console.warn(
        "[Button] icon-only button without `tooltip` or `aria-label` — screen readers cannot identify this button."
      );
    }

    const cls = cn(
      buttonVariants({ variant: effectiveVariant, size: effectiveSize }),
      width === "full" && "w-full",
      tooltip && isIconOnly && "relative group/tooltip",
      className
    );

    const tooltipSpan =
      tooltip && isIconOnly ? (
        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded px-2 py-1 font-secondary-body text-background bg-foreground shadow-01 opacity-0 group-hover/tooltip:opacity-100 transition-opacity z-50">
          {tooltip}
        </span>
      ) : null;

    const content = (
      <>
        {LeftIcon && <LeftIcon className="h-4 w-4 shrink-0" />}
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        {isLoading ? (
          <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
        ) : (
          children
        )}
        {RightIcon && <RightIcon className="h-4 w-4 shrink-0" />}
        {tooltipSpan}
      </>
    );

    if (href) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={href}
          target={target}
          className={cls}
          title={tooltip}
          aria-label={derivedAriaLabel}
        >
          {content}
        </Link>
      );
    }

    if (asChild) {
      return (
        <Slot ref={ref as React.Ref<HTMLButtonElement>} className={cls} {...rest}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        disabled={disabled || isLoading}
        className={cls}
        title={tooltip}
        aria-label={derivedAriaLabel}
        {...rest}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
